import type { ModelSelection } from "@cleo/contracts";
// TR2/TR3 — the typed room controller (validated rebuild audit §3
// amendment 3). The room's utility-surface doors and the governed-birth door
// used to ride two window CustomEvent buses whose module globals stood in
// for room truth: a subscriber counter meaning "a governed room is seated"
// and a one-shot pending-section slot. The buses' replacement is typed
// commands with scope lifetime, not a better bus (useAtomCommand and
// VS Code's CommandsRegistry are the reference shapes): callers invoke a
// command and receive delivered/unseated as a VALUE, so every fallback
// (routed page, teaching toast) stays a caller decision; seats are
// registrations that live exactly as long as their scope and return their
// own disposer.
//
// The section-addressing laws carry over from the bus verbatim:
//   - every open overwrites the pending section (plain opens clear it), so a
//     stale address can never hijack a later open;
//   - a section delivered LIVE to a mounted surface clears the pending slot,
//     so a future mount cannot re-consume it;
//   - listening for sections is never a seat — it must not make the doors
//     believe a room is governed;
//   - a surface that mounts because of the open consumes the pending section
//     exactly once.
type SettingsPath =
  | "/settings/account"
  | "/settings/privacy"
  | "/settings/general"
  | "/settings/world"
  | "/settings/appearance"
  | "/settings/keybindings"
  | "/settings/subscription-accounts"
  | "/settings/provider-keys"
  | "/settings/local-models"
  | "/settings/instructions"
  | "/settings/connections"
  | "/settings/permissions"
  | "/settings/archived";

export type WorldViewUtilityBoard =
  | "settings"
  | "usage"
  | "music"
  | "calendar"
  | "inbox"
  | "kanban";

/**
 * The Inbox's communication rails, addressable from outside the surface.
 * "decisions" (W-a67b0503) is the decisions door: Cleo's requests still
 * waiting on the person, the list a count of waiting decisions opens.
 */
export type InboxRailAddress =
  | "decisions"
  | "hitl"
  | "world"
  | "gmail"
  | "alias"
  | "imessage"
  | "slack";

export interface WorldViewBoardRequest {
  readonly kind: WorldViewUtilityBoard;
  readonly settingsSection?: SettingsPath;
  readonly inboxRail?: InboxRailAddress;
  /** With the decisions address: the one request to open. */
  readonly inboxGate?: string;
  /**
   * W-a67b0503: the address is served again to an Inbox of this one session
   * that remounts (its deck restore replaces the deck after the door opened
   * it), while `stands` answers true: the door passes its deck front
   * request's standing.
   */
  readonly inboxHold?: InboxAddressHold;
}

export interface GovernedSessionBirthRequest {
  /**
   * The provider and model the ceremony opens with already chosen — the
   * picker's D1 door (a thread bound to one driver kind cannot switch, so
   * the person starts a new session on the provider they clicked).
   */
  readonly modelSelection?: ModelSelection;
  /** Exact Agent rail whose current session becomes the new child's parent. */
  readonly parentId?: string;
  /** The World anchors a top-level session; every descendant snapshots a parent. */
  readonly birthSource?: "world" | "parent";
  /**
   * SB3: birth the session AS this workroom — the ordinary governed
   * ceremony, with the board's body fronted and its doctrine layered at
   * provider session start. Only meaningful with birthSource "world".
   */
  readonly workroomKey?: string;
  /** RM9: the ROOM a world-parented session is born into (a workroom's chip names it). */
  readonly roomId?: string;
}

const surfaceSeats = new Set<(kind: WorldViewUtilityBoard) => void>();
const sectionListeners = new Set<(section: SettingsPath) => void>();
const birthSeats = new Set<(request: GovernedSessionBirthRequest) => void>();
let pendingSettingsSection: SettingsPath | null = null;
const inboxRailListeners = new Set<(rail: InboxRailAddress, gate: string | null) => void>();
let pendingInboxRail: InboxRailAddress | null = null;
let pendingInboxGate: string | null = null;
export interface InboxAddressHold {
  readonly scope: { readonly worldKey: string; readonly sessionId: string };
  readonly stands: () => boolean;
}
let heldInbox: {
  readonly rail: InboxRailAddress;
  readonly gate: string | null;
  readonly hold: InboxAddressHold;
} | null = null;

/** True while an addressed room's Canvas is seated to host utility surfaces. */
export function worldViewBoardsSeated(): boolean {
  return surfaceSeats.size > 0;
}

function openWorldRoomSurface(request: WorldViewBoardRequest): boolean {
  pendingSettingsSection = request.settingsSection ?? null;
  // The same laws as the section slot: every open overwrites, a live
  // delivery clears, a mount consumes exactly once.
  pendingInboxRail = request.inboxRail ?? null;
  pendingInboxGate = request.inboxGate ?? null;
  // Every Inbox open replaces the held address; other surfaces leave it.
  if (request.kind === "inbox")
    heldInbox =
      request.inboxRail !== undefined && request.inboxHold !== undefined
        ? { rail: request.inboxRail, gate: request.inboxGate ?? null, hold: request.inboxHold }
        : null;
  const delivered = surfaceSeats.size > 0;
  for (const seat of [...surfaceSeats]) seat(request.kind);
  if (request.kind === "inbox" && request.inboxRail !== undefined) {
    for (const listener of [...inboxRailListeners]) {
      pendingInboxRail = null;
      pendingInboxGate = null;
      listener(request.inboxRail, request.inboxGate ?? null);
    }
  }
  // Section listeners hear the address independently of canvas seats — a
  // mounted Settings surface takes the section live even in routed mode,
  // exactly as the window event reached both listener classes on the bus.
  if (request.kind === "settings" && request.settingsSection !== undefined) {
    for (const listener of [...sectionListeners]) {
      // Delivered live; the pending slot must not re-deliver it to a future
      // mount of the surface.
      pendingSettingsSection = null;
      listener(request.settingsSection);
    }
  }
  return delivered;
}

export function openWorldViewSettings(): boolean {
  return openWorldRoomSurface({ kind: "settings" });
}

export function openWorldViewUsage(): boolean {
  return openWorldRoomSurface({ kind: "usage" });
}

/**
 * The music singleton, fronted in the ADDRESSED room — always at that
 * level, never nested (SP3). Callers keep the unseated answer: the strip
 * treats false as a quiet no-op, the apps-group door may teach.
 */
export function openWorldViewMusic(): boolean {
  return openWorldRoomSurface({ kind: "music" });
}

/**
 * The calendar singleton (TM14), fronted in the ADDRESSED room exactly like
 * Music: no routed page exists, so an unseated false is the caller's quiet
 * no-op.
 */
export function openWorldViewCalendar(): boolean {
  return openWorldRoomSurface({ kind: "calendar" });
}

/**
 * The World Board singleton (BOARD1, register §38), fronted in the ADDRESSED
 * room exactly like Music and Calendar: no routed page exists, so an unseated
 * false is the caller's quiet no-op.
 */
export function openWorldViewKanban(): boolean {
  return openWorldRoomSurface({ kind: "kanban" });
}

/**
 * The Inbox singleton (CM4), fronted in the ADDRESSED room: the top-right
 * door stops leaving the app when a governed room is seated. Callers keep
 * the unseated answer for their own fallback.
 */
export function openWorldViewInbox(): boolean {
  return openWorldRoomSurface({ kind: "inbox" });
}

/** The Inbox fronted AT a rail — the pinned tab's rail doors (2026-09-03). */
export function openWorldViewInboxRail(
  rail: InboxRailAddress,
  gate?: string,
  hold?: InboxAddressHold,
): boolean {
  return openWorldRoomSurface({
    kind: "inbox",
    inboxRail: rail,
    ...(gate === undefined ? {} : { inboxGate: gate }),
    ...(hold === undefined ? {} : { inboxHold: hold }),
  });
}

/** The person chose the Inbox's filter: a remount no longer restores the door's address. */
export function releaseHeldInboxAddress(): void {
  heldInbox = null;
}

/**
 * FD44 (register FD-CAN-44, 2026-09-05): the workroom door. Chrome that
 * holds neither the router nor the room index — the context dock's Music
 * seat, the strip's expand doors, a transcript card — opens a workroom
 * through this one door: the room's existing session fronted, or the room
 * born on first use (RM9: "click to restore existing"). The
 * WorldViewProvider seats the one opener; unseated (Local, loading,
 * unavailable) answers false so the caller may teach. A workroom opens
 * with its own Cleo rail — this door never drops the room's board as a tab
 * into the addressed general room (GOALS-ROOMS: launched by its chip, never
 * by `+`).
 */
/**
 * What a door asks of the room it opens. With no intent the room restores the
 * tab its session last fronted. `tab: "main"` asks for the room's own board
 * instead (the Orchestrator's ruling, installed 0.0.193, 2026-10-07: the rail
 * player's expand opens the Music room on its main tab).
 */
export interface WorkroomOpenIntent {
  readonly tab: "main";
}

type WorkroomSeat = (
  workroomKey: string,
  worldKey?: string,
  intent?: WorkroomOpenIntent,
) => boolean;
const workroomSeats = new Set<WorkroomSeat>();

export function seatWorkroomDoor(open: WorkroomSeat): () => void {
  workroomSeats.add(open);
  return () => {
    workroomSeats.delete(open);
  };
}

export function openWorldViewWorkroom(
  workroomKey: string,
  worldKey?: string,
  intent?: WorkroomOpenIntent,
): boolean {
  for (const seat of [...workroomSeats]) {
    if (seat(workroomKey, worldKey, intent)) return true;
  }
  return false;
}

/** The Music room — every music expand door's one target (FD44). */
export function openWorldViewMusicRoom(intent?: WorkroomOpenIntent): boolean {
  return openWorldViewWorkroom("music", undefined, intent);
}

/** Rail addressing for a mounted Inbox surface. Not a seat. */
export function onWorldViewInboxRail(
  listener: (rail: InboxRailAddress, gate: string | null) => void,
): () => void {
  inboxRailListeners.add(listener);
  return () => {
    inboxRailListeners.delete(listener);
  };
}

/** One-shot read of the rail a request addressed before the surface mounted. */
export function consumeWorldRoomInboxRail(): InboxRailAddress | null {
  return consumeWorldRoomInboxAddress().rail;
}

/** The session the window's route stands in (/session/<id>, hash or path routing), or null. */
export function routeSessionId(): string | null {
  if (typeof window === "undefined") return null;
  const { hash, pathname } = window.location;
  const path = hash.startsWith("#/") ? hash.slice(1) : pathname;
  const match = /^\/session\/([^/?#]+)/.exec(path);
  if (match === null) return null;
  try {
    return decodeURIComponent(match[1]!);
  } catch {
    return null;
  }
}

/**
 * The rail and the one request (decisions door) a request addressed before the
 * mount, read once. `at` is the World and session the mounting Inbox stands in;
 * only an Inbox of the session the door opened takes a held address again.
 */
export function consumeWorldRoomInboxAddress(
  at: { readonly worldKey: string | null; readonly sessionId: string | null } = {
    worldKey: null,
    sessionId: null,
  },
): {
  readonly rail: InboxRailAddress | null;
  readonly gate: string | null;
} {
  const address = { rail: pendingInboxRail, gate: pendingInboxGate };
  pendingInboxRail = null;
  pendingInboxGate = null;
  if (address.rail !== null) return address;
  // W-a67b0503: an Inbox remounted by its session's deck restore takes the
  // address the door opened it at, for as long as that door's front stands.
  if (heldInbox !== null) {
    if (!heldInbox.hold.stands()) heldInbox = null;
    else if (
      heldInbox.hold.scope.worldKey === at.worldKey &&
      heldInbox.hold.scope.sessionId === at.sessionId
    )
      return { rail: heldInbox.rail, gate: heldInbox.gate };
  }
  return address;
}

/** Settings surface, addressed at its Subscription accounts section. */
export function openWorldRoomProviders(): boolean {
  return openWorldRoomSurface({
    kind: "settings",
    settingsSection: "/settings/subscription-accounts",
  });
}

/** Settings surface, addressed at its Provider keys section (personal API connections). */
export function openWorldRoomProviderKeys(): boolean {
  return openWorldRoomSurface({ kind: "settings", settingsSection: "/settings/provider-keys" });
}

export function openWorldRoomLocalModels(): boolean {
  return openWorldRoomSurface({ kind: "settings", settingsSection: "/settings/local-models" });
}

/** Settings surface, addressed at its Connections section. */
export function openWorldRoomConnections(): boolean {
  return openWorldRoomSurface({ kind: "settings", settingsSection: "/settings/connections" });
}

/** Settings surface, addressed at its Account section. */
export function openWorldRoomAccount(): boolean {
  return openWorldRoomSurface({ kind: "settings", settingsSection: "/settings/account" });
}

/**
 * Seat the addressed room's Canvas as the utility-surface host. The seat
 * lives exactly as long as the returned disposer is unheld.
 */
export function seatWorldRoomSurfaces(seat: (kind: WorldViewUtilityBoard) => void): () => void {
  surfaceSeats.add(seat);
  return () => {
    surfaceSeats.delete(seat);
  };
}

/**
 * Section addressing for a Settings surface that is already mounted. Not a
 * seat: registering here must not make the doors believe a room is governed.
 */
export function onWorldRoomSettingsSection(listener: (section: SettingsPath) => void): () => void {
  sectionListeners.add(listener);
  return () => {
    sectionListeners.delete(listener);
  };
}

/** One-shot read of the section a request addressed before the surface mounted. */
export function consumeWorldRoomSettingsSection(): SettingsPath | null {
  const section = pendingSettingsSection;
  pendingSettingsSection = null;
  return section;
}

/**
 * Ask the seated governed-birth ceremony host to open. Returns false when no
 * host is seated (Local mode, or a fronted World whose room has not read
 * back) so the caller keeps its fallback — the teaching toast stays a caller
 * decision, never a silent dispatch into the void.
 */
export function openGovernedSessionBirth(request: GovernedSessionBirthRequest = {}): boolean {
  if (birthSeats.size === 0) return false;
  for (const seat of [...birthSeats]) seat(request);
  return true;
}

/** Seat the governed-birth ceremony host for exactly its mounted lifetime. */
export function seatGovernedSessionBirth(
  seat: (request: GovernedSessionBirthRequest) => void,
): () => void {
  birthSeats.add(seat);
  return () => {
    birthSeats.delete(seat);
  };
}
